/**
 * ClaimScoreCard — Score preview card for the claim page.
 *
 * Shows the "teaser" data that hooks the founder:
 * - Company name + investor attribution
 * - ScoreRing with animated overall score
 * - Score label (Highly Investable / Investment Ready / etc.)
 * - Confidence % with ShieldCheck icon
 * - Top risk flag with AlertTriangle icon
 * - Locked/blurred section teasing full breakdown, recommendations, and risks
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { ShieldCheck, AlertTriangle, Lock, BarChart3, Lightbulb, Shield } from 'lucide-react-native';
import Animated, { FadeInUp, FadeInDown } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { getScoreColor } from '@/lib/colors';
import { ScoreRing } from '@/components/startup/DashboardWidgets';

// ============================================================================
// Types
// ============================================================================

interface ClaimScoreCardProps {
  companyName: string;
  overallScore: number;
  confidence: number;
  topRiskFlag: string | null;
  investorName: string;
  /** If true, show blurred/locked sections for paid features */
  showUpgradeTeaser?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

function getScoreLabel(score: number): string {
  if (score >= 80) return 'Highly Investable';
  if (score >= 60) return 'Investment Ready';
  if (score >= 40) return 'Developing';
  return 'Early Stage';
}

function getConfidenceLabel(confidence: number): string {
  if (confidence >= 80) return 'High';
  if (confidence >= 60) return 'Moderate';
  return 'Low';
}

// ============================================================================
// Component
// ============================================================================

export function ClaimScoreCard({
  companyName,
  overallScore,
  confidence,
  topRiskFlag,
  investorName,
  showUpgradeTeaser = true,
}: ClaimScoreCardProps) {
  const colors = useThemeColors();
  const scoreColor = getScoreColor(overallScore);

  return (
    <View>
      {/* Main Score Card */}
      <Animated.View
        entering={FadeInUp.delay(100).duration(600)}
        style={{
          backgroundColor: colors.surface.primary,
          borderRadius: 28,
          padding: 24,
          shadowColor: colors.primary[500],
          shadowOpacity: 0.12,
          shadowRadius: 24,
          shadowOffset: { width: 0, height: 8 },
          elevation: 8,
        }}
      >
        {/* Header */}
        <Animated.View entering={FadeInDown.delay(200).duration(500)}>
          <Text
            className="text-2xl font-bold"
            style={{
              color: colors.text.primary,
              marginBottom: 4,
            }}
          >
            {companyName}
          </Text>
          <Text
            className="text-sm"
            style={{
              color: colors.text.tertiary,
              marginBottom: 20,
            }}
          >
            Scored via {investorName}&apos;s submission link
          </Text>
        </Animated.View>

        {/* Score Ring + Label */}
        <Animated.View
          entering={FadeInUp.delay(300).duration(600)}
          style={{ alignItems: 'center', marginBottom: 20 }}
        >
          <ScoreRing score={overallScore} size={160} />
          <View
            style={{
              marginTop: 12,
              paddingHorizontal: 16,
              paddingVertical: 8,
              borderRadius: 20,
              backgroundColor: `${scoreColor}15`,
            }}
          >
            <Text
              className="text-base font-semibold"
              style={{ color: scoreColor }}
            >
              {getScoreLabel(overallScore)}
            </Text>
          </View>
        </Animated.View>

        {/* Confidence & Risk Mini Cards */}
        <Animated.View
          entering={FadeInUp.delay(400).duration(500)}
          style={{
            flexDirection: 'row',
            gap: 12,
            marginTop: 4,
            paddingTop: 16,
            borderTopWidth: 1,
            borderTopColor: colors.border.light,
          }}
        >
          {/* Confidence */}
          <View
            style={{
              flex: 1,
              backgroundColor: colors.surface.secondary,
              borderRadius: 16,
              padding: 14,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <ShieldCheck size={14} color={colors.primary[500]} />
              <Text
                className="text-xs font-semibold"
                style={{
                  color: colors.text.tertiary,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Confidence
              </Text>
            </View>
            <Text
              className="text-2xl font-bold"
              style={{ color: colors.text.primary }}
            >
              {confidence}%
            </Text>
            <Text
              className="text-xs font-medium"
              style={{
                color: confidence >= 60 ? colors.success : colors.warning,
                marginTop: 2,
              }}
            >
              {getConfidenceLabel(confidence)}
            </Text>
          </View>

          {/* Risk Flag */}
          <View
            style={{
              flex: 1,
              backgroundColor: colors.surface.secondary,
              borderRadius: 16,
              padding: 14,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <AlertTriangle size={14} color={topRiskFlag ? colors.warning : colors.success} />
              <Text
                className="text-xs font-semibold"
                style={{
                  color: colors.text.tertiary,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Top Risk
              </Text>
            </View>
            <Text
              className="text-base font-bold"
              style={{ color: topRiskFlag ? colors.warning : colors.success }}
              numberOfLines={2}
            >
              {topRiskFlag || 'No Major Risks'}
            </Text>
          </View>
        </Animated.View>

        {/* Locked Content Teaser */}
        {showUpgradeTeaser && (
          <Animated.View entering={FadeInUp.delay(500).duration(500)}>
            {/* Full Breakdown */}
            <LockedSection
              icon={BarChart3}
              title="Full Breakdown"
              subtitle="5-category score analysis"
              colors={colors}
              barCount={5}
            />

            {/* AI Recommendations */}
            <LockedSection
              icon={Lightbulb}
              title="AI Recommendations"
              subtitle="Personalized improvement tips"
              colors={colors}
              barCount={4}
            />

            {/* Risk Analysis */}
            <LockedSection
              icon={Shield}
              title="Risk Analysis"
              subtitle="Detailed risk assessment"
              colors={colors}
              barCount={3}
            />
          </Animated.View>
        )}
      </Animated.View>
    </View>
  );
}

// ============================================================================
// Locked Section Sub-component
// ============================================================================

function LockedSection({
  icon: Icon,
  title,
  subtitle,
  colors,
  barCount,
}: {
  icon: React.ComponentType<{ size: number; color: string }>;
  title: string;
  subtitle: string;
  colors: ReturnType<typeof useThemeColors>;
  barCount: number;
}) {
  const barWidths: Array<`${number}%`> = ['80%', '60%', '70%', '50%', '90%'];

  return (
    <View
      style={{
        position: 'relative',
        overflow: 'hidden',
        marginTop: 16,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      {/* Fake content (not real data) */}
      <View style={{ opacity: 0.15, padding: 16 }}>
        {barWidths.slice(0, barCount).map((width, i) => (
          <View
            key={i}
            style={{
              height: 12,
              backgroundColor: colors.text.tertiary,
              borderRadius: 6,
              width,
              marginBottom: i < barCount - 1 ? 8 : 0,
            }}
          />
        ))}
      </View>

      {/* Lock overlay */}
      <View
        style={{
          ...StyleSheet.absoluteFillObject,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: `${colors.surface.primary}CC`,
          borderRadius: 16,
        }}
      >
        <View
          style={{
            width: 44,
            height: 44,
            borderRadius: 22,
            backgroundColor: `${colors.primary[500]}15`,
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 6,
          }}
        >
          <Lock size={20} color={colors.primary[500]} />
        </View>
        <Text
          className="text-sm font-semibold"
          style={{ color: colors.text.primary }}
        >
          {title}
        </Text>
        <Text
          className="text-xs"
          style={{
            color: colors.text.secondary,
            textAlign: 'center',
            marginTop: 2,
          }}
        >
          {subtitle}
        </Text>
      </View>
    </View>
  );
}
